import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  Translator,
  buildResourceBundle,
  directionFor,
  flattenResources,
  resolveLocale,
  type TextDirection,
} from '@tabita/i18n';

/**
 * Locale state for the whole client.
 *
 * Two things happen here that are easy to get wrong and expensive to retrofit:
 *
 * 1. `lang` and `dir` are set on the document element, not on a wrapper div.
 *    Screen readers and the browser's own text shaping read them from the root,
 *    and `dir` on a nested element does not fix a page whose root says `ltr`.
 *
 * 2. The chosen locale is persisted. A user who selected Urdu must find Urdu on
 *    their next visit, before any network call resolves their profile.
 */
const STORAGE_KEY = 'tabita.locale';

/**
 * All function members are arrow-function properties. Consumers destructure
 * them, and method shorthand would declare an implicit `this` that is lost
 * when the function is detached from its object.
 */
interface I18nValue {
  readonly locale: string;
  readonly direction: TextDirection;
  readonly t: (
    key: string,
    options?: {
      values?: Record<string, string | number>;
      count?: number;
      /** Rendered when the key is missing in every locale in the chain. */
      fallback?: string;
    },
  ) => string;
  readonly setLocale: (locale: string) => void;
  /**
   * Adopts the locale recorded on the signed-in user's profile.
   *
   * Deliberately weaker than `setLocale`: it yields to a choice already made on
   * this device and does not persist. The precedence the platform promises —
   * explicit choice, then profile, then browser — only holds if a profile
   * cannot silently overwrite what someone picked from the menu.
   */
  readonly applyProfileLocale: (locale: string | null | undefined) => void;
  readonly isLoading: boolean;
}

const I18nContext = createContext<I18nValue | null>(null);

/**
 * Resource bundles are loaded on demand.
 *
 * Ten locales bundled together would ship nine unused translation files to
 * every visitor. Vite's glob import produces one chunk per locale.
 */
const bundles = import.meta.glob<Record<string, unknown>>(
  '../../../../packages/i18n/locales/*.json',
);

async function loadResources(locale: string): Promise<Record<string, string>> {
  const path = `../../../../packages/i18n/locales/${locale}.json`;
  const loader = bundles[path];
  if (!loader) return {};
  const module = (await loader()) as { default?: Record<string, unknown> } & Record<
    string,
    unknown
  >;
  return flattenResources(module.default ?? module);
}

function readStoredLocale(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private browsing and blocked site data both throw here. A missing
    // preference is not an error worth surfacing.
    return null;
  }
}

export function I18nProvider({ children }: { children: ReactNode }): ReactNode {
  const [locale, setLocaleState] = useState(
    () =>
      resolveLocale({
        userLocale: readStoredLocale(),
        acceptLanguage: navigator.languages.join(','),
      }).code,
  );

  const [resources, setResources] = useState<Record<string, Record<string, string>>>({});
  const [isLoading, setIsLoading] = useState(true);
  /**
   * True until the first locale bundle has loaded.
   *
   * Children are not rendered before that. Without this the whole interface
   * paints its raw translation keys — a login screen reading "auth.signIn" —
   * for as long as the JSON takes to arrive. A subsequent locale *switch* does
   * not blank the screen: the previous strings stay visible until the new ones
   * replace them, which is far less jarring than a flash of empty layout.
   */
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setIsLoading(true);
      // English is always loaded alongside, as the end of the fallback chain.
      // A missing key must degrade to readable English, never to a raw key
      // shown to a congregation member.
      const [active, fallback] = await Promise.all([
        loadResources(locale),
        locale === DEFAULT_LOCALE ? Promise.resolve({}) : loadResources(DEFAULT_LOCALE),
      ]);

      if (cancelled) return;
      setResources(buildResourceBundle(locale, active, fallback));
      setIsLoading(false);
      setHasLoadedOnce(true);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const direction = directionFor(locale);

  useEffect(() => {
    // Set on the root element: this is what the browser's bidi algorithm and
    // assistive technology actually read.
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
  }, [locale, direction]);

  const setLocale = useCallback((next: string) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies for this session.
    }
  }, []);

  const applyProfileLocale = useCallback((profileLocale: string | null | undefined) => {
    if (!profileLocale) return;
    // Read at call time, not at mount: someone may have used the picker since.
    if (readStoredLocale()) return;
    // Through the resolver, so a profile recorded as `es-MX` reaches `es`
    // rather than falling all the way to English.
    setLocaleState(resolveLocale({ userLocale: profileLocale }).code);
  }, []);

  const translator = useMemo(
    () =>
      new Translator(resources, locale, (key) => {
        if (import.meta.env.DEV) {
          // Loud in development, silent in production: a half-translated screen
          // is recoverable, a crashed one is not.
          console.warn(`[i18n] missing key "${key}" for locale "${locale}"`);
        }
      }),
    [resources, locale],
  );

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      direction,
      t: (key, options) => translator.t(key, options),
      setLocale,
      applyProfileLocale,
      isLoading,
    }),
    [locale, direction, translator, setLocale, applyProfileLocale, isLoading],
  );

  return (
    <I18nContext.Provider value={value}>{hasLoadedOnce ? children : null}</I18nContext.Provider>
  );
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error('useI18n must be used inside an I18nProvider');
  }
  return value;
}

/** Shorthand for the common case of translating a single key. */
export function useTranslate(): I18nValue['t'] {
  return useI18n().t;
}

export { SUPPORTED_LOCALES };
